import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';

// Define supported wallet types
export const WALLET_TYPES = {
  METAMASK: 'metamask',
  TRUST_WALLET: 'trustwallet',
  BINANCE_WALLET: 'binancewallet',
  TOKEN_POCKET: 'tokenpocket',
  SAFEPAL: 'safepal'
};

const WalletConnector = ({ 
  onConnect, 
  onDisconnect,
  onError,
  onChainChanged,
  requiredChainId = 204, // opBNB chain ID
  buttonClassName = "", // Optional class for custom styling
  account = null // Add account prop to sync with parent state
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [connectedAccount, setConnectedAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [walletType, setWalletType] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);

  // Check for existing connections on component mount
  useEffect(() => {
    checkExistingConnection();
  }, []);

  // Set up event listeners when provider changes
  useEffect(() => {
    if (provider) {
      setupEventListeners();
    }
    return () => {
      removeEventListeners();
    }
  }, [provider]);

  // Check if the user was previously connected
  const checkExistingConnection = async () => {
    try {
      // Check local storage for saved wallet type
      const savedWalletType = localStorage.getItem('walletType');
      
      if (savedWalletType && window.ethereum) {
        // Attempt to reconnect with the saved wallet type
        await connectToWallet(savedWalletType, true);
      } else if (window.ethereum) {
        // Check if already connected to MetaMask
        const ethProvider = new ethers.providers.Web3Provider(window.ethereum);
        const accounts = await ethProvider.listAccounts();
        
        if (accounts && accounts.length > 0) {
          setProvider(ethProvider);
          setConnectedAccount(accounts[0]);
          setWalletType(WALLET_TYPES.METAMASK);
          
          // Notify parent component
          if (onConnect) {
            onConnect(accounts[0], ethProvider, WALLET_TYPES.METAMASK);
          }
        }
      }
    } catch (error) {
      console.error("Error checking existing connection:", error);
    }
  };

  // Set up event listeners for the connected wallet
  const setupEventListeners = () => {
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);
      window.ethereum.on('disconnect', handleDisconnect);
    }
  };

  // Remove event listeners when component unmounts or wallet disconnects
  const removeEventListeners = () => {
    if (window.ethereum) {
      window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      window.ethereum.removeListener('chainChanged', handleChainChanged);
      window.ethereum.removeListener('disconnect', handleDisconnect);
    }
  };

  // Handle account changes
  const handleAccountsChanged = (accounts) => {
    if (accounts.length === 0) {
      // User has disconnected all accounts
      handleDisconnect();
    } else {
      // User has switched accounts
      setConnectedAccount(accounts[0]);
      if (onConnect) {
        onConnect(accounts[0], provider, walletType);
      }
    }
  };

  // Handle chain changes
  const handleChainChanged = (chainId) => {
    // Normalize chainId to decimal
    let normalizedChainId;
    
    if (typeof chainId === 'string') {
      normalizedChainId = parseInt(chainId, 16);
    } else {
      normalizedChainId = chainId;
    }
    
    if (normalizedChainId !== requiredChainId) {
      setError(`Please switch to opBNB (Chain ID: ${requiredChainId})`);
    } else {
      setError(null);
    }
    
    // Reset provider to handle chain change properly
    if (window.ethereum) {
      setProvider(new ethers.providers.Web3Provider(window.ethereum));
    }
    
    // Notify parent component
    if (onChainChanged) {
      onChainChanged(normalizedChainId);
    }
  };

  // Handle wallet disconnection
  const handleDisconnect = () => {
    // Clear state
    setConnectedAccount(null);
    setProvider(null);
    setWalletType(null);
    
    // Remove from local storage
    localStorage.removeItem('walletType');
    
    // Notify parent component
    if (onDisconnect) {
      onDisconnect();
    }
  };

  // Connect to a wallet
  const connectToWallet = async (type, isReconnect = false) => {
    try {
      setIsConnecting(true);
      setError(null);
      
      // Check if the wallet is installed
      if (!window.ethereum) {
        if (type === WALLET_TYPES.METAMASK) {
          throw new Error("MetaMask is not installed. Please install MetaMask to continue.");
        } else {
          throw new Error(`${type} is not detected. Please open this site in the ${type} browser.`);
        }
      }
      
      // Request accounts
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts'
      });
      
      if (!accounts || accounts.length === 0) {
        throw new Error("No accounts found. Please connect your wallet.");
      }
      
      // Check and switch network if needed
      await switchToRequiredNetwork();
      
      // Create provider
      const web3Provider = new ethers.providers.Web3Provider(window.ethereum);
      
      // Save connection information
      setProvider(web3Provider);
      setConnectedAccount(accounts[0]);
      setWalletType(type);
      
      // Save wallet type to local storage for reconnection
      localStorage.setItem('walletType', type);
      
      // Close modal
      setIsModalOpen(false);
      
      // Notify parent component
      if (onConnect) {
        onConnect(accounts[0], web3Provider, type);
      }
    } catch (error) {
      console.error(`Error connecting to ${type}:`, error);
      
      // Format error message
      const errorMessage = error.message || `Failed to connect to ${type}`;
      setError(errorMessage);
      
      // Notify parent component
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsConnecting(false);
    }
  };

  // Switch to the required network
  const switchToRequiredNetwork = async () => {
    if (!window.ethereum) return false;
    
    try {
      // Try to switch to opBNB
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${requiredChainId.toString(16)}` }], // Convert to hex
      });
      return true;
    } catch (switchError) {
      // Network doesn't exist in wallet
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: `0x${requiredChainId.toString(16)}`, // Convert to hex
                chainName: requiredChainId === 204 ? 'opBNB' : 'opBNB Testnet',
                nativeCurrency: {
                  name: 'BNB',
                  symbol: 'BNB',
                  decimals: 18
                },
                rpcUrls: [requiredChainId === 204 ? 'https://opbnb-mainnet-rpc.bnbchain.org/' : 'https://opbnb-testnet-rpc.bnbchain.org/'],
                blockExplorerUrls: [requiredChainId === 204 ? 'https://opbnbscan.com' : 'https://testnet.opbnbscan.com']
              },
            ],
          });
          return true;
        } catch (addError) {
          console.error('Error adding opBNB network', addError);
          throw addError;
        }
      }
      console.error('Error switching network', switchError);
      throw switchError;
    }
  };

  // Disconnect wallet
  const disconnectWallet = () => {
    // Call general disconnect handler
    handleDisconnect();
  };

  // Simple wallet icon component (replace with actual images)
  const WalletIcon = ({ name }) => (
    <div style={{
      width: '2rem',
      height: '2rem',
      backgroundColor: '#4F46E5',
      borderRadius: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '0.75rem',
      fontWeight: 'bold',
      marginRight: '0.75rem'
    }}>
      {name.slice(0, 2).toUpperCase()}
    </div>
  );

  // Render wallet connection button or connected account button
  const renderConnectButton = () => {
    // Use account prop first, then fall back to internal connectedAccount state
    const displayAccount = account || connectedAccount;
    
    if (displayAccount) {
      // Show wallet address when connected
      return (
        <button
          className={buttonClassName || 'default-wallet-btn'}
          onClick={() => setIsModalOpen(true)}
        >
          {`${displayAccount.slice(0, 6)}...${displayAccount.slice(-4)}`}
        </button>
      );
    } else {
      // Show "Connect Wallet" when not connected
      return (
        <button
          className={buttonClassName || 'default-wallet-btn'}
          onClick={() => setIsModalOpen(true)}
          disabled={isConnecting}
        >
          {isConnecting ? 'Connecting...' : 'Connect Wallet'}
        </button>
      );
    }
  };

  // Render wallet selection modal
  const renderWalletModal = () => {
    if (!isModalOpen) return null;
    
    // Use account prop first, then fall back to internal connectedAccount state
    const displayAccount = account || connectedAccount;
    
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        padding: '1rem'
      }}>
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '1rem',
          padding: '1.5rem',
          maxWidth: '28rem',
          width: '100%',
          margin: '0 auto',
          position: 'relative',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}>
          <button
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              color: '#6B7280',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              fontSize: '1.5rem'
            }}
            onClick={() => setIsModalOpen(false)}
          >
            ×
          </button>
          
          <h2 style={{
            fontSize: '1.25rem',
            fontWeight: 'bold',
            color: '#111827',
            marginBottom: '1.5rem'
          }}>
            {displayAccount ? 'Wallet Connected' : 'Connect Wallet'}
          </h2>
          
          {error && (
            <div style={{
              marginBottom: '1rem',
              padding: '0.75rem',
              backgroundColor: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: '0.5rem',
              color: '#DC2626',
              fontSize: '0.875rem'
            }}>
              {error}
            </div>
          )}
          
          {displayAccount ? (
            <div>
              <div style={{
                marginBottom: '1.5rem',
                padding: '1rem',
                backgroundColor: '#F3F4F6',
                borderRadius: '0.5rem'
              }}>
                <p style={{
                  fontSize: '0.875rem',
                  color: '#6B7280',
                  marginBottom: '0.25rem'
                }}>Connected Account</p>
                <p style={{
                  fontFamily: 'monospace',
                  color: '#111827',
                  wordBreak: 'break-all',
                  margin: 0
                }}>{displayAccount}</p>
              </div>
              
              <button
                style={{
                  width: '100%',
                  padding: '0.75rem 0',
                  backgroundColor: '#DC2626',
                  borderRadius: '0.5rem',
                  color: '#FFFFFF',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  border: 'none',
                  outline: 'none'
                }}
                onClick={disconnectWallet}
              >
                Disconnect Wallet
              </button>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: '0.75rem'
            }}>
              {/* Wallet buttons */}
              {[
                { type: WALLET_TYPES.METAMASK, name: 'MetaMask' },
                { type: WALLET_TYPES.TRUST_WALLET, name: 'Trust Wallet' },
                { type: WALLET_TYPES.BINANCE_WALLET, name: 'Binance Wallet' },
                { type: WALLET_TYPES.SAFEPAL, name: 'SafePal' },
                { type: WALLET_TYPES.TOKEN_POCKET, name: 'Token Pocket' }
              ].map(wallet => (
                <button
                  key={wallet.type}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '1rem',
                    backgroundColor: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '0.5rem',
                    transition: 'background-color 0.2s',
                    cursor: 'pointer',
                    outline: 'none'
                  }}
                  onClick={() => connectToWallet(wallet.type)}
                  disabled={isConnecting}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#F3F4F6'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#F9FAFB'}
                >
                  <WalletIcon name={wallet.name} />
                  <span style={{
                    fontWeight: '500',
                    color: '#111827'
                  }}>{wallet.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {renderConnectButton()}
      {renderWalletModal()}
      
      {/* Add default styles if no custom class is provided */}
      {!buttonClassName && (
        <style jsx>{`
          .default-wallet-btn {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.6rem 1.5rem;
            font-size: 0.9rem;
            font-weight: 600;
            border: none;
            border-radius: 25px;
            background: linear-gradient(135deg, #f59e0b, #d97706);
            color: white;
            cursor: pointer;
            transition: all 0.3s ease;
            box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
            white-space: nowrap;
            min-height: 40px;
          }
          
          .default-wallet-btn:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(245, 158, 11, 0.4);
            background: linear-gradient(135deg, #d97706, #b45309);
          }
          
          .default-wallet-btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
          }
        `}</style>
      )}
    </>
  );
};

export default WalletConnector;